/**
 * 가장 가까운 스크롤 컨테이너(overflow:auto/scroll)의 경계를 반환.
 * 없으면 viewport 기준 반환.
 * @param {HTMLElement} el
 * @returns {{ top: number, bottom: number, left: number, right: number }}
 */
function getScrollContainerRect(el) {
  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    const style = getComputedStyle(parent);
    const overflow = style.overflow + style.overflowY + style.overflowX;
    if (/auto|scroll|hidden/.test(overflow)) {
      const rect = parent.getBoundingClientRect();
      return { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right };
    }
    parent = parent.parentElement;
  }
  return { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };
}

/**
 * 드롭다운/메뉴를 viewport 안에 맞게 자동 위치 조정
 * @param {HTMLElement} trigger  - 메뉴를 여는 버튼/요소
 * @param {HTMLElement} dropdown - 열리는 메뉴 컨테이너
 * @param {number}      minGap   - viewport 가장자리와의 최소 여백 (px, 기본 8)
 */
function positionDropdown(trigger, dropdown, minGap = 8) {
  dropdown.classList.remove('dd-flip-right', 'dd-flip-up');
  dropdown.querySelectorAll('[data-dd-item]').forEach(el => {
    el.style.paddingTop = '';
    el.style.paddingBottom = '';
  });

  const tr = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // inline style 및 computed style 모두 확인 (Bootstrap class 기반 display:none 포함)
  const computedStyle = getComputedStyle(dropdown);
  const wasHidden =
    computedStyle.display === 'none' ||
    computedStyle.visibility === 'hidden' ||
    dropdown.style.display === 'none' ||
    dropdown.style.visibility === 'hidden';

  if (wasHidden) {
    dropdown.style.visibility = 'hidden';
    dropdown.style.display = 'block';
  }
  const dd = dropdown.getBoundingClientRect();
  if (wasHidden) {
    dropdown.style.visibility = '';
    dropdown.style.display = '';
  }

  // dd가 아직 렌더되지 않아 크기가 0이면 측정 포기
  if (dd.width === 0 && dd.height === 0) return;

  // 스크롤 컨테이너 경계와 viewport 중 더 제한적인 쪽 사용
  const container = getScrollContainerRect(dropdown);
  const clampBottom = Math.min(vh, container.bottom);
  const clampRight  = Math.min(vw, container.right);
  const clampLeft   = Math.max(0, container.left);

  // ── 수평 처리 ──────────────────────────────
  const overflowRight = tr.left + dd.width > clampRight - minGap;
  const overflowLeft  = tr.right - dd.width < clampLeft + minGap;
  if (overflowRight && !overflowLeft) {
    dropdown.classList.add('dd-flip-right');
  }

  // ── 수직 처리 ──────────────────────────────
  const spaceBelow = clampBottom - tr.bottom - minGap;
  const spaceAbove = tr.top - Math.max(0, container.top) - minGap;

  if (dd.height > spaceBelow) {
    if (spaceAbove >= dd.height) {
      dropdown.classList.add('dd-flip-up');
    } else {
      const excess  = dd.height - spaceBelow;
      const items   = dropdown.querySelectorAll('[data-dd-item]');
      if (items.length > 0) {
        const shrink = excess / items.length;
        const MIN_PAD = 2;
        items.forEach(el => {
          const currentPad = parseFloat(getComputedStyle(el).paddingTop) || 8;
          const newPad = Math.max(MIN_PAD, currentPad - shrink / 2);
          el.style.paddingTop    = newPad + 'px';
          el.style.paddingBottom = newPad + 'px';
        });
      }
    }
  }
}

/**
 * 서브메뉴 항목들이 컨테이너를 넘치지 않도록 폰트·패딩 자동 압축.
 * 컨테이너에 [data-submenu-item] 속성을 가진 자식 요소가 있어야 동작.
 * @param {HTMLElement} containerEl - 서브메뉴 행 컨테이너
 * @param {number}      minFontSize - 최소 폰트 크기 (px, 기본 11)
 */
function fitSubmenuItems(containerEl, minFontSize = 11) {
  const items = containerEl.querySelectorAll('[data-submenu-item]');
  if (!items.length) return;

  // 스타일 초기화
  items.forEach(el => {
    el.style.fontSize = '';
    el.style.paddingLeft = '';
    el.style.paddingRight = '';
  });

  const cStyle = getComputedStyle(containerEl);
  const gap = parseFloat(cStyle.gap) || parseFloat(cStyle.columnGap) || 8;
  const padH = parseFloat(cStyle.paddingLeft) + parseFloat(cStyle.paddingRight);
  const minGap = 4;

  let fontSize = parseFloat(getComputedStyle(items[0]).fontSize) || 14;

  // 항목 전체 너비(패딩 포함) + gap + 컨테이너 패딩 계산
  const getTotalWidth = () => {
    let w = padH + gap * (items.length - 1);
    items.forEach(el => { w += el.offsetWidth; });
    return w;
  };

  while (getTotalWidth() > containerEl.clientWidth && fontSize > minFontSize) {
    fontSize -= 0.5;
    items.forEach(el => {
      el.style.fontSize = fontSize + 'px';
      const curPad = parseFloat(getComputedStyle(el).paddingLeft) || 20;
      el.style.paddingLeft  = Math.max(minGap, curPad - 1) + 'px';
      el.style.paddingRight = Math.max(minGap, curPad - 1) + 'px';
    });
  }
}

export { fitSubmenuItems };
export default positionDropdown;
